'use client'

import {
  Box,
  Button,
  Card,
  Container,
  Field,
  Heading,
  Input,
  InputGroup,
  Portal,
  Select,
  Stack,
  Text,
  createListCollection,
} from '@chakra-ui/react'
import { forwardRef, useState } from 'react'
import { FiMail, FiUser } from 'react-icons/fi'
import type {
  IRCompanyConfig,
  IRThemeConfig,
  InvestorRelationsConfigContextType,
} from '@/utils/investorRelations'
import { useInvestorRelationsPublicAPI } from '@/utils/investorRelationsPublic'

interface RegisterInterestSectionProps {
  companySlug: string
  useContextFunction: () => InvestorRelationsConfigContextType
}

const ACCOUNT_TYPE_COLLECTION = createListCollection({
  items: [
    { value: 'investor', label: 'Investor' },
    { value: 'lender', label: 'Lender' },
  ],
})

export const RegisterInterestSection = forwardRef<
  HTMLElement,
  RegisterInterestSectionProps
>(function RegisterInterestSection({ companySlug, useContextFunction }, ref) {
  const { config, loading, error } =
    useContextFunction() as InvestorRelationsConfigContextType
  const themeConfig = ((config?.themeConfig as IRThemeConfig) ||
    {}) as IRThemeConfig
  const primaryColor = themeConfig.primaryColor
  const textColor = themeConfig.textColor
  const fontFamily = themeConfig.fontStyle

  const companyConfig = ((config?.companyConfig as IRCompanyConfig) ||
    {}) as IRCompanyConfig
  const companyName = companyConfig.companyName
  const { registerInterest } = useInvestorRelationsPublicAPI()

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [accountType, setAccountType] = useState<'investor' | 'lender'>(
    'investor'
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim() || !email.trim()) {
      return
    }

    setIsSubmitting(true)

    try {
      // TODO: Replace with actual API call to register interest
      const response = await registerInterest(
        companySlug,
        name.trim(),
        email.trim(),
        accountType
      )
      setIsSubmitted(true)
      setName('')
      setEmail('')
      setAccountType('investor')
    } catch (error) {
      console.error('Failed to register interest:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const isValidName = (name: string) => {
    const trimmed = name.trim()
    // At least 2 characters, only letters, spaces, hyphens, and apostrophes
    // Allows names like "John Doe", "Mary-Jane", "O'Connor"
    return (
      trimmed.length >= 2 &&
      /^[a-zA-Z\s'-]+$/.test(trimmed) &&
      trimmed.length <= 100
    )
  }

  const isFormValid =
    name.trim() !== '' &&
    email.trim() !== '' &&
    isValidEmail(email) &&
    isValidName(name)

  return (
    <Box
      ref={ref}
      as="section"
      id="register-interest"
      bg={`${primaryColor}1A`}
      minH="calc(50vh - 25px)"
      display="flex"
      alignItems="center"
      py={{ base: 10, md: 16 }}
    >
      <Container maxW="6xl">
        <Stack
          gap={{ base: 6, md: 8 }}
          align="center"
          textAlign="center"
          minH="calc(50vh - 25px)"
          display="flex"
          alignItems="center"
        >
          <Heading
            as="h2"
            size={{ base: 'lg', md: '5xl' }}
            color={primaryColor}
            fontWeight="bold"
            mb={3}
            textAlign="center"
            fontFamily={fontFamily}
          >
            Register Your Interest
          </Heading>

          <Text
            fontSize={{ base: 'sm', md: 'md' }}
            color={textColor}
            maxW="xl"
            fontFamily={fontFamily}
          >
            Stay updated with the latest news and communications from{' '}
            {companyName}
          </Text>

          <Card.Root
            w="full"
            maxW="md"
            borderRadius="lg"
            boxShadow="lg"
            bg="white"
            p={{ base: 6, md: 8 }}
          >
            {isSubmitted ? (
              <Stack gap={4} align="center" py={4}>
                <Text
                  fontSize="lg"
                  color="green.700"
                  fontWeight="semibold"
                  fontFamily={fontFamily}
                >
                  Thank you for registering your interest!
                </Text>
                <Text fontSize="sm" color="gray.600" fontFamily={fontFamily}>
                  We'll keep you updated with the latest news and
                  communications.
                </Text>
                <Button
                  onClick={() => setIsSubmitted(false)}
                  color={primaryColor}
                  variant="outline"
                  size="sm"
                  fontFamily={fontFamily}
                >
                  Register Another
                </Button>
              </Stack>
            ) : (
              <form onSubmit={handleSubmit}>
                <Stack gap={6}>
                  <Field.Root>
                    <Field.Label
                      display="flex"
                      alignItems="center"
                      gap={2}
                      color="gray.700"
                      fontSize="sm"
                      fontWeight="medium"
                      fontFamily={fontFamily}
                    >
                      <FiUser size={18} />
                      Name
                    </Field.Label>
                    <InputGroup>
                      <Input
                        maxLength={100}
                        type="text"
                        placeholder="Enter your full name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        bg="white"
                        borderColor="gray.300"
                        _focus={{
                          borderColor: 'green.500',
                          boxShadow: '0 0 0 1px var(--chakra-colors-green-500)',
                        }}
                      />
                    </InputGroup>
                    {name && !isValidName(name) && (
                      <Field.ErrorText fontSize="xs" fontFamily={fontFamily}>
                        Please enter a valid name
                      </Field.ErrorText>
                    )}
                  </Field.Root>

                  <Field.Root>
                    <Field.Label
                      display="flex"
                      alignItems="center"
                      gap={2}
                      color="gray.700"
                      fontSize="sm"
                      fontWeight="medium"
                      fontFamily={fontFamily}
                    >
                      <FiMail size={18} />
                      Email
                    </Field.Label>
                    <InputGroup>
                      <Input
                        type="email"
                        placeholder="Enter your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        bg="white"
                        borderColor="gray.300"
                        _focus={{
                          borderColor: 'green.500',
                          boxShadow: '0 0 0 1px var(--chakra-colors-green-500)',
                        }}
                      />
                    </InputGroup>
                    {email && !isValidEmail(email) && (
                      <Field.ErrorText fontSize="xs" fontFamily={fontFamily}>
                        Please enter a valid email address
                      </Field.ErrorText>
                    )}
                  </Field.Root>

                  <Field.Root>
                    <Field.Label
                      color="gray.700"
                      fontSize="sm"
                      fontWeight="medium"
                      fontFamily={fontFamily}
                    >
                      Type
                    </Field.Label>
                    <Select.Root
                      collection={ACCOUNT_TYPE_COLLECTION}
                      value={[accountType]}
                      onValueChange={(details) =>
                        setAccountType(
                          details.value[0] === 'lender' ? 'lender' : 'investor'
                        )
                      }
                    >
                      <Select.HiddenSelect />
                      <Select.Control>
                        <Select.Trigger bg="white" borderColor="gray.300">
                          <Select.ValueText placeholder="Select type" />
                        </Select.Trigger>
                        <Select.IndicatorGroup>
                          <Select.Indicator />
                        </Select.IndicatorGroup>
                      </Select.Control>
                      <Portal>
                        <Select.Positioner>
                          <Select.Content>
                            {ACCOUNT_TYPE_COLLECTION.items.map((item) => (
                              <Select.Item key={item.value} item={item}>
                                <Select.ItemText>{item.label}</Select.ItemText>
                                <Select.ItemIndicator />
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Positioner>
                      </Portal>
                    </Select.Root>
                  </Field.Root>

                  <Button
                    type="submit"
                    bg={primaryColor}
                    color="white"
                    variant="solid"
                    size="lg"
                    w="full"
                    borderRadius="md"
                    fontWeight="bold"
                    loading={isSubmitting}
                    disabled={!isFormValid || isSubmitting}
                    fontFamily={fontFamily}
                  >
                    Register Interest
                  </Button>
                </Stack>
              </form>
            )}
          </Card.Root>
        </Stack>
      </Container>
    </Box>
  )
})
